import { useState, useEffect } from 'react';
import { conditionsAPI, seriesAPI, divisionsAPI, teamsAPI } from '../../services/api';
import { PageHeader, SectionTitle, Field, Spinner, DayPicker } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const TIME_SLOTS = ['8:45 AM', '9:00 AM', '10:30 AM', '11:00 AM', '12:30 PM', '2:30 PM', '4:00 PM', '5:30 PM'];
const HA_OPTIONS = ['Strict equal', '5H/5A', '5H/4A or 4H/5A', 'Flexible'];
const SECTION_TYPES = ['game_day', 'time_slot', 'ground_rule', 'bye', 'home_away', 'team_specific', 'free_text'];
const SECTION_LABELS = {
  game_day: 'Game Day Rules', time_slot: 'Time Slot Rules', ground_rule: 'Ground Rules',
  bye: 'Bye Rules', home_away: 'Home/Away Balance', team_specific: 'Team-Specific Overrides', free_text: 'Free Text Conditions'
};
const SECTION_COLORS = {
  game_day: 'var(--accent)', time_slot: 'var(--blue)', ground_rule: 'var(--blue)',
  bye: 'var(--gold)', home_away: 'var(--purple)', team_specific: 'var(--mid)', free_text: 'var(--mid)'
};

export default function ConditionsPage() {
  const { canEdit } = useAuthStore();
  const [seriesList, setSeriesList] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [selSeries, setSelSeries] = useState('');
  const [selDivision, setSelDivision] = useState('');
  const [activeSection, setActiveSection] = useState('game_day');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Structured form state
  const [playDays, setPlayDays] = useState([]);
  const [gamesPerDay, setGamesPerDay] = useState('1');
  const [timeSlotsSelected, setTimeSlotsSelected] = useState([]);
  const [homeAway, setHomeAway] = useState('Flexible');
  const [minByes, setMinByes] = useState('1');
  const [maxByes, setMaxByes] = useState('2');

  // Free-entry inputs
  const [newGroundRule, setNewGroundRule] = useState('');
  const [newFreeText, setNewFreeText] = useState('');
  const [newTeamOverride, setNewTeamOverride] = useState({ team_id: '', override_type: '', rule: '' });

  useEffect(() => { loadSeries(); }, []);
  useEffect(() => { if (selSeries) { loadDivisions(selSeries); loadTeams(selSeries); loadConditions(); } }, [selSeries]);
  useEffect(() => { if (selSeries) loadConditions(); }, [selDivision]);

  const loadSeries = async () => {
    try {
      const { data } = await seriesAPI.list();
      setSeriesList(data);
      if (data.length > 0) setSelSeries(data[0].id);
    } catch { toast.error('Failed to load series'); }
  };

  const loadDivisions = async (sid) => {
    try { const { data } = await divisionsAPI.list(sid); setDivisions(data); } catch {}
  };

  const loadTeams = async (sid) => {
    try { const { data } = await teamsAPI.list({ series_id: sid }); setTeams(data); } catch {}
  };

  const loadConditions = async () => {
    setLoading(true);
    try {
      const params = { series_id: selSeries };
      if (selDivision) params.division_id = selDivision;
      const { data } = await conditionsAPI.list(params);
      setConditions(data);
      // Hydrate structured fields
      data.forEach(c => {
        if (c.condition_type === 'game_day') setPlayDays(c.value?.days || []);
        if (c.condition_type === 'games_per_day') setGamesPerDay(String(c.value?.max || 1));
        if (c.condition_type === 'time_slot') setTimeSlotsSelected(c.value?.slots || []);
        if (c.condition_type === 'home_away') setHomeAway(c.value?.balance || 'Flexible');
        if (c.condition_type === 'bye_min') setMinByes(String(c.value?.min || 1));
        if (c.condition_type === 'bye_max') setMaxByes(String(c.value?.max || 2));
      });
    } catch { toast.error('Failed to load conditions'); }
    finally { setLoading(false); }
  };

  const saveStructured = async () => {
    setSaving(true);
    try {
      const base = { series_id: selSeries, division_id: selDivision || null };
      const toSave = [
        { ...base, condition_type: 'game_day',     condition_key: 'C1', label: 'Games Played On',       value: { days: playDays } },
        { ...base, condition_type: 'games_per_day', condition_key: 'C2', label: 'Games Per Team Per Day', value: { max: parseInt(gamesPerDay) } },
        { ...base, condition_type: 'time_slot',     condition_key: 'C3', label: 'Allowed Game Times',     value: { slots: timeSlotsSelected } },
        { ...base, condition_type: 'home_away',     condition_key: 'C4', label: 'Home/Away Balance',      value: { balance: homeAway } },
        { ...base, condition_type: 'bye_min',       condition_key: 'C9',  label: 'Min Byes Per Team',      value: { min: parseInt(minByes) } },
        { ...base, condition_type: 'bye_max',       condition_key: 'C10', label: 'Max Byes Per Team',      value: { max: parseInt(maxByes) } },
      ];
      for (const c of toSave) {
        const existing = conditions.find(x => x.condition_key === c.condition_key && x.series_id === selSeries);
        if (existing) await conditionsAPI.update(existing.id, c);
        else await conditionsAPI.create(c);
      }
      toast.success('Structured conditions saved');
      loadConditions();
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const addFreeEntry = async (type, value, label) => {
    try {
      const existing = conditions.filter(c => c.condition_type === type && c.series_id === selSeries);
      const nextNum = existing.length + 1;
      await conditionsAPI.create({
        series_id: selSeries, division_id: selDivision || null,
        condition_type: type, condition_key: `${type.toUpperCase().slice(0,2)}${nextNum}`,
        label, value: { text: value }, sort_order: nextNum
      });
      toast.success('Condition added');
      loadConditions();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to add'); }
  };

  const deleteCondition = async (id) => {
    try { await conditionsAPI.delete(id); toast.success('Removed'); loadConditions(); }
    catch { toast.error('Failed to remove'); }
  };

  const toggleTimeSlot = (slot) => setTimeSlotsSelected(p => p.includes(slot) ? p.filter(x => x !== slot) : [...p, slot]);

  const groundRules = conditions.filter(c => c.condition_type === 'ground_rule');
  const freeTextConds = conditions.filter(c => c.condition_type === 'free_text');
  const teamSpecific = conditions.filter(c => c.condition_type === 'team_specific');

  const condCount = (type) => conditions.filter(c => c.condition_type === type).length;

  return (
    <div>
      <PageHeader icon="⧉" title="Conditions"
        subtitle="Scheduling rules and constraints per series / division."
        actions={canEdit() && (
          <button className="btn btn-primary" onClick={saveStructured} disabled={saving}>
            {saving ? <><Spinner size={14} /> Saving...</> : '✓ Save Conditions'}
          </button>
        )}
      />

      <div style={{ background: 'rgba(59,130,246,.08)', border: '1px solid rgba(59,130,246,.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 12, color: 'var(--mid)' }}>
        <strong style={{ color: 'var(--blue)' }}>ℹ Note:</strong> Ground rules are free text — no static ground names hardcoded. Special matchup rules come from the <strong>Matchup Matrix</strong> page. C13-type "must play on date" rules use free text.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '210px 1fr', gap: 20 }}>
        {/* Sidebar */}
        <div className="card" style={{ padding: 16, alignSelf: 'start' }}>
          <SectionTitle>Context</SectionTitle>
          <Field label="Series">
            <select className="input select" value={selSeries} onChange={e => { setSelSeries(e.target.value); setSelDivision(''); }}>
              {seriesList.map(s => <option key={s.id} value={s.id}>{s.short_name} {s.season_year}</option>)}
            </select>
          </Field>
          <Field label="Division">
            <select className="input select" value={selDivision} onChange={e => setSelDivision(e.target.value)}>
              <option value="">All Divisions</option>
              {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          <hr className="divider" />
          <SectionTitle color="var(--gold)">Condition Types</SectionTitle>
          {SECTION_TYPES.map(type => (
            <div key={type} onClick={() => setActiveSection(type)}
              style={{ padding: '7px 10px', borderRadius: 6, marginBottom: 3, cursor: 'pointer', fontSize: 12, fontWeight: activeSection === type ? 700 : 400, color: activeSection === type ? SECTION_COLORS[type] : 'var(--mid)', background: activeSection === type ? `rgba(0,200,150,.1)` : 'transparent', border: `1px solid ${activeSection === type ? SECTION_COLORS[type] : 'transparent'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {SECTION_LABELS[type]}
              {condCount(type) > 0 && <span className="mono" style={{ fontSize: 10, background: 'rgba(255,255,255,.1)', padding: '1px 6px', borderRadius: 10 }}>{condCount(type)}</span>}
            </div>
          ))}
        </div>

        {/* Main panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {loading ? (
            <div className="card" style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><Spinner size={28} /></div>
          ) : (
            <>
              {/* Game Day + Time Slots */}
              {(activeSection === 'game_day' || activeSection === 'time_slot' || activeSection === 'home_away') && (
                <div className="card" style={{ padding: 20 }}>
                  <SectionTitle>Structured Conditions</SectionTitle>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Field label="C1 · Games Played On (Days)" required>
                        <DayPicker value={playDays} onChange={setPlayDays} />
                      </Field>
                    </div>
                    <Field label="C2 · Games Per Team Per Day">
                      <div style={{ display: 'flex', gap: 6 }}>
                        {['1','2','3'].map(n => (
                          <button key={n} type="button"
                            className={`btn btn-sm ${gamesPerDay === n ? 'btn-primary' : 'btn-ghost'}`}
                            onClick={() => setGamesPerDay(n)}>{n}</button>
                        ))}
                      </div>
                    </Field>
                    <Field label="C4 · Home / Away Balance">
                      <select className="input select" value={homeAway} onChange={e => setHomeAway(e.target.value)}>
                        {HA_OPTIONS.map(o => <option key={o}>{o}</option>)}
                      </select>
                    </Field>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Field label="C3 · Allowed Game Times" note="Click to toggle allowed slots">
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {TIME_SLOTS.map(t => (
                            <div key={t} onClick={() => toggleTimeSlot(t)}
                              style={{ padding: '5px 12px', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 600, border: `1px solid ${timeSlotsSelected.includes(t) ? 'var(--accent)' : 'var(--border)'}`, background: timeSlotsSelected.includes(t) ? 'rgba(0,200,150,.15)' : 'transparent', color: timeSlotsSelected.includes(t) ? 'var(--accent)' : 'var(--dim)', transition: 'all .12s' }}>
                              {t}
                            </div>
                          ))}
                        </div>
                        {timeSlotsSelected.length > 0 && <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 6 }}>{timeSlotsSelected.length} slot{timeSlotsSelected.length > 1 ? 's' : ''} selected</div>}
                      </Field>
                    </div>
                  </div>
                  {canEdit() && (
                    <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                      <button className="btn btn-primary" onClick={saveStructured} disabled={saving}>
                        {saving ? 'Saving...' : '✓ Save Structured Conditions'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Bye Rules */}
              {activeSection === 'bye' && (
                <div className="card" style={{ padding: 20 }}>
                  <SectionTitle color="var(--gold)">Bye Rules</SectionTitle>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <Field label="C9 · Min Byes Per Team">
                      <input className="input" value={minByes} onChange={e => setMinByes(e.target.value)} placeholder="e.g. 1" />
                    </Field>
                    <Field label="C10 · Max Byes Per Team">
                      <input className="input" value={maxByes} onChange={e => setMaxByes(e.target.value)} placeholder="e.g. 2" />
                    </Field>
                  </div>
                  {canEdit() && (
                    <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                      <button className="btn btn-primary" onClick={saveStructured} disabled={saving}>✓ Save Bye Rules</button>
                    </div>
                  )}
                </div>
              )}

              {/* Ground Rules */}
              {activeSection === 'ground_rule' && (
                <div className="card" style={{ padding: 20 }}>
                  <SectionTitle color="var(--blue)">Ground Rules — Free Text</SectionTitle>
                  <p style={{ fontSize: 12, color: 'var(--mid)', marginBottom: 14 }}>
                    Type any ground-specific rule. E.g. "Only 1 game per weekend at Troy", "Trombly: 2 games per day at 10:30 AM and 2:30 PM", "Max 2 games per ground per team across the season."
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    {groundRules.length === 0 && <div style={{ fontSize: 12, color: 'var(--dim)', padding: '8px 0' }}>No ground rules yet.</div>}
                    {groundRules.map((r, i) => (
                      <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--border)' }}>
                        <span className="mono tag tag-blue" style={{ flexShrink: 0, marginTop: 1 }}>C{i + 5}</span>
                        <span style={{ fontSize: 12, color: 'var(--text)', flex: 1 }}>{r.value?.text}</span>
                        {canEdit() && <button onClick={() => deleteCondition(r.id)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14, padding: '0 4px', flexShrink: 0 }}>✕</button>}
                      </div>
                    ))}
                  </div>
                  {canEdit() && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input className="input" style={{ flex: 1 }} placeholder="e.g. 'Belle Isle: max 3 games per day'" value={newGroundRule} onChange={e => setNewGroundRule(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && newGroundRule.trim()) { addFreeEntry('ground_rule', newGroundRule, 'Ground Rule'); setNewGroundRule(''); } }} />
                      <button className="btn btn-primary" style={{ flexShrink: 0 }}
                        onClick={() => { if (newGroundRule.trim()) { addFreeEntry('ground_rule', newGroundRule, 'Ground Rule'); setNewGroundRule(''); } }}>+ Add</button>
                    </div>
                  )}
                </div>
              )}

              {/* Team-Specific */}
              {activeSection === 'team_specific' && (
                <div className="card" style={{ padding: 20 }}>
                  <SectionTitle color="var(--mid)">Team-Specific Overrides</SectionTitle>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    {teamSpecific.length === 0 && <div style={{ fontSize: 12, color: 'var(--dim)', padding: '8px 0' }}>No team overrides yet.</div>}
                    {teamSpecific.map(r => (
                      <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--border)' }}>
                        <span className="tag tag-blue mono">{r.value?.team_short || '—'}</span>
                        <span className="tag tag-purple">{r.value?.override_type || 'Rule'}</span>
                        <span style={{ fontSize: 12, color: 'var(--mid)', flex: 1 }}>{r.value?.text}</span>
                        {canEdit() && <button onClick={() => deleteCondition(r.id)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>✕</button>}
                      </div>
                    ))}
                  </div>
                  {canEdit() && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: 8, alignItems: 'end' }}>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--mid)', marginBottom: 4 }}>Team</div>
                        <select className="input select" value={newTeamOverride.team_id} onChange={e => setNewTeamOverride(p => ({ ...p, team_id: e.target.value }))}>
                          <option value="">Select team...</option>
                          {teams.map(t => <option key={t.id} value={t.id}>{t.short_name}</option>)}
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--mid)', marginBottom: 4 }}>Type</div>
                        <select className="input select" value={newTeamOverride.override_type} onChange={e => setNewTeamOverride(p => ({ ...p, override_type: e.target.value }))}>
                          <option value="">Type...</option>
                          <option>Time</option><option>Ground</option><option>Bye</option><option>Other</option>
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--mid)', marginBottom: 4 }}>Rule</div>
                        <input className="input" placeholder="e.g. 12:30 PM only" value={newTeamOverride.rule}
                          onChange={e => setNewTeamOverride(p => ({ ...p, rule: e.target.value }))} />
                      </div>
                      <button className="btn btn-primary" onClick={async () => {
                        if (!newTeamOverride.team_id || !newTeamOverride.rule) return toast.error('Select team and enter rule');
                        const team = teams.find(t => t.id === newTeamOverride.team_id);
                        await addFreeEntry('team_specific', newTeamOverride.rule, `${team?.short_name} Override`);
                        setNewTeamOverride({ team_id: '', override_type: '', rule: '' });
                      }}>+ Add</button>
                    </div>
                  )}
                </div>
              )}

              {/* Free Text */}
              {activeSection === 'free_text' && (
                <div className="card" style={{ padding: 20 }}>
                  <SectionTitle color="var(--mid)">💬 Free Text Conditions</SectionTitle>
                  <p style={{ fontSize: 12, color: 'var(--mid)', marginBottom: 14 }}>
                    Use for any rule not covered above — "must play on date" rules, cross-series notes, playoff conditions, or any custom requirement. Each entry is auto-numbered and linked to the selected series/division.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                    {freeTextConds.length === 0 && <div style={{ fontSize: 12, color: 'var(--dim)', padding: '8px 0' }}>No free text conditions yet.</div>}
                    {freeTextConds.map((c, i) => (
                      <div key={c.id} style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, color: 'var(--text)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <span className="mono tag tag-purple" style={{ flexShrink: 0 }}>C{i + 12}</span>
                          <span>{c.value?.text}</span>
                        </div>
                        {canEdit() && <button onClick={() => deleteCondition(c.id)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14, padding: '0 4px', flexShrink: 0 }}>✕</button>}
                      </div>
                    ))}
                  </div>
                  {canEdit() && (
                    <>
                      <textarea className="input" rows={3} placeholder="Type any condition... e.g. 'All 14 Premier teams must play on 08/23/2026' or 'No back-to-back away games for top 3 teams in August'"
                        style={{ resize: 'vertical', marginBottom: 8 }} value={newFreeText} onChange={e => setNewFreeText(e.target.value)} />
                      <button className="btn btn-ghost" style={{ fontSize: 12 }}
                        onClick={() => { if (newFreeText.trim()) { addFreeEntry('free_text', newFreeText, 'Free Text Condition'); setNewFreeText(''); } }}>
                        + Add Free Text Condition
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Matchup Rules (read-only reference) */}
              <div className="card" style={{ padding: 20 }}>
                <SectionTitle color="var(--purple)">Special Matchup Rules — via Matchup Matrix</SectionTitle>
                <div style={{ background: 'rgba(59,130,246,.06)', border: '1px solid rgba(59,130,246,.15)', borderRadius: 7, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--mid)' }}>
                  <strong style={{ color: 'var(--blue)' }}>ℹ Note:</strong> Matchup rules (e.g. DRCC vs SUSC only in August) are configured in the <strong>Matchup Matrix</strong> page. This panel is read-only.
                </div>
                {conditions.filter(c => c.condition_type === 'matchup').length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 12 }}>No matchup restrictions configured yet. Set them in the Matchup Matrix.</div>
                ) : (
                  conditions.filter(c => c.condition_type === 'matchup').map(r => (
                    <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--border)', marginBottom: 6 }}>
                      <span className="tag tag-blue mono">{r.value?.team1}</span>
                      <span style={{ fontSize: 11, color: 'var(--mid)' }}>vs</span>
                      <span className="tag tag-blue mono">{r.value?.team2}</span>
                      <span style={{ fontSize: 11, color: 'var(--mid)' }}>→</span>
                      <span className="tag tag-purple">{r.value?.restriction}</span>
                      <span style={{ fontSize: 11, color: 'var(--dim)', marginLeft: 'auto', fontStyle: 'italic' }}>From matrix</span>
                    </div>
                  ))
                )}
                <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => window.location.assign('/matrix')}>→ Open Matchup Matrix</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
